/**
 * Creates a deeply reactive proxy around `target`. Any property
 * mutation (set, delete) on the proxy or any nested object/array
 * triggers the `onChange` callback exactly once (caller is expected
 * to coalesce via queueMicrotask or similar).
 *
 * Nested proxies are created lazily on first access and cached via
 * WeakMap so repeated reads return the same proxy instance. When an
 * object is replaced, old proxy wrappers become eligible for GC.
 */
export function createReactiveProxy<T extends object>(
  target: T,
  onChange: () => void
): T {
  const proxyCache = new WeakMap<object, object>();

  function wrap(obj: object): object {
    const cached = proxyCache.get(obj);
    if (cached) return cached;

    const proxy = new Proxy(obj, {
      get(t, prop, receiver) {
        const value = Reflect.get(t, prop, receiver);

        if (
          value !== null &&
          typeof value === "object" &&
          !Object.isFrozen(value) &&
          typeof prop !== "symbol"
        ) {
          return wrap(value);
        }

        return value;
      },

      set(t, prop, value, receiver) {
        const old = Reflect.get(t, prop, receiver);
        const result = Reflect.set(t, prop, value, receiver);
        if (result && !Object.is(old, value)) {
          onChange();
        }
        return result;
      },

      deleteProperty(t, prop) {
        const had = prop in t;
        const result = Reflect.deleteProperty(t, prop);
        if (result && had) {
          onChange();
        }
        return result;
      },
    });

    proxyCache.set(obj, proxy);
    return proxy;
  }

  return wrap(target) as T;
}
